import { IconSearchOff } from '@tabler/icons-react';
import { DataTable, type DataTableColumn, type DataTableRowExpansionProps } from 'mantine-datatable';
import { motion } from 'motion/react';

import { containerTransition } from '#core/components/dashboard/panels/shared/constants.ts';

import classes from './Table.module.css';

export interface TableRendererProps {
  title?: string;
  columns: DataTableColumn[];
  records: Record<string, unknown>[];
  pinFirstColumn?: boolean;

  // Selection props
  selectedRecords?: Record<string, unknown>[];
  onSelectedRecordsChange?: (records: Record<string, unknown>[]) => void;
  idAccessor?: (record: Record<string, unknown>) => string;

  // Expansion props
  rowExpansion?: DataTableRowExpansionProps<Record<string, unknown>>;
}

/**
 * Default ID accessor for records - uses the pre-computed `id` field from transformToTableProps.
 */
function defaultIdAccessor(record: Record<string, unknown>): string {
  return record.id as string;
}

/**
 * Internal table renderer used by DashboardTable.
 * Wraps mantine-datatable with consistent styling and interactive features.
 */
export function TableRenderer({
  title,
  columns,
  records,
  pinFirstColumn,
  selectedRecords,
  onSelectedRecordsChange,
  idAccessor = defaultIdAccessor,
  rowExpansion,
}: TableRendererProps) {
  return (
    <div className={classes.tableContainer}>
      {title && <div className={classes.tableHeader}>{title}</div>}
      <motion.div
        animate="show"
        className={classes.dataTableContainer}
        exit="exit"
        initial="hidden"
        layout
        variants={containerTransition}
      >
        <DataTable
          columns={columns}
          defaultColumnProps={{ noWrap: true }}
          idAccessor={idAccessor}
          noRecordsIcon={<IconSearchOff />}
          noRecordsText="No matching rows"
          onSelectedRecordsChange={onSelectedRecordsChange}
          pinFirstColumn={pinFirstColumn}
          records={records}
          rowExpansion={rowExpansion}
          selectedRecords={selectedRecords}
          withTableBorder={false}
        />
      </motion.div>
    </div>
  );
}
